'use client';

import React, { Suspense, lazy, useEffect, useContext } from 'react';
import { VibeshipContext } from '../contexts/VibeshipContext';
import { ErrorBoundary } from './ErrorBoundary';

// Lazy load the UI component
const VibeshipDevTools = lazy(() => {
  return import('./VibeshipDevTools').then(module => ({
    default: module.VibeshipDevTools
  }));
});

/**
 * Client-side provider that handles all interactive features.
 * This component must be marked as 'use client'.
 */
export function VibeshipClientProvider() {
  const context = useContext(VibeshipContext);
  
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    // Check if we should show in production
    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction && !context?.config?.ui?.showInProduction) {
      return;
    }
    
    // Log that the provider is initialized
    console.log('[VibeshipDevTools] Initialized with config:', context?.config);
    
    // Global keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Default to cmd+shift+k for the panel toggle
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        console.log('[VibeshipDevTools] Keyboard shortcut triggered: cmd+shift+k');
        
        // Dispatch custom event for the panel to listen to
        window.dispatchEvent(new CustomEvent('vibeship:toggle-panel'));
      }
      
      // Command palette shortcut (cmd+k)
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        console.log('[VibeshipDevTools] Command palette shortcut triggered: cmd+k');
        
        // Dispatch custom event for command palette
        window.dispatchEvent(new CustomEvent('vibeship:toggle-command-palette'));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [context]);
  
  // Don't render in production unless explicitly enabled
  const isProduction = process.env.NODE_ENV === 'production';
  if (isProduction && !context?.config?.ui?.showInProduction) {
    return null;
  }
  
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <VibeshipDevTools />
      </Suspense>
    </ErrorBoundary>
  );
}